import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { ChevronDown } from 'lucide-react';

type FaqItem = {
  question: string;
  answer: string;
  table?: { metric: string; value: string }[];
};

const faqItems: FaqItem[] = [
  {
    question: "How are test participants selected?",
    answer: "Tastermonial maintains an ever-growing pool of recurring users who test products for their own health benefits and interest. While we cannot guarantee product testing due to public interest, we make our best effort to balance incentives for optimal test results. Testers are free to choose which products to test, and we incentivize participation without direct reimbursement to maintain unbiased reviews."
  },
  {
    question: "What's included in the testing protocol?",
    answer: "Our standard testing protocol includes product shipping, participant onboarding, 14-day continuous glucose monitoring, demographic data collection, sentiment analysis, and verified testimonial generation. Premium tiers include additional metrics and custom reporting."
  },
  {
    question: "What do I need to provide?",
    answer: "We recommend brands to send their products to us so that we may pack and send it for free to the testers. This ensures a higher rate of participation and faster feedback. Furthermore, your product and experiment need to be registered in the dashboard so that users can join the experiment and be guided through the process. Our team is here to assist you in configuring the experiments in the best way for success."
  },
  {
    question: "Can I choose specific influencers?",
    answer: "At this point, we do not offer the ability to choose specific influencers. However, when setting up your product experiment, you are free to clarify the intented demographic of the participants to ensure the most relevant feedback."
  },
  {
    question: "What if my product doesn't perform well?",
    answer: "Transparency is our priority. You'll receive all test data regardless of outcomes. In order to ensure unbiased results, we do not reward participants to leave positive reviews."
  },
  {
    question: "How is participant data protected?",
    answer: "We follow strict HIPAA-compliant protocols for all health data. Participants provide explicit consent for sharing anonymized glucose data and testimonials. Personal identifiers are never shared without express permission."
  },
  {
    question: "What's your refund policy?",
    answer: "Credits that are purchased are valid for one year, after which a refund may be requested at 70% of the purchase price."
  },
  {
    question: "How Many Samples Do You Need?",
    answer: `Send at least 300 product samples for a target of 10 validated test completions, based on these requirements:
• We select 10 participants to sponsor the CGM and recruit up to 90 more self-pay CGM users from our Biohacker Community.`,
    table: [
      { metric: 'Target: Completed Test Results', value: '10' },
      { metric: 'Expected Completion Rate', value: '1 in 3 testers' },
      { metric: 'Testers Needed', value: '30' },
      { metric: 'Samples per Tester', value: '10' },
      { metric: 'Total Samples to Send', value: '300' },
    ]
  }
];

const AnswerText: React.FC<{ text: string; table?: { metric: string; value: string }[] }> = ({ text, table }) => {
  const lines = text.split('\n').map((line, index) => {
    if (line.trim().startsWith('•')) {
      return (
        <div key={index} className="flex items-start gap-2">
          <span className="text-neutral-700">•</span>
          <span className="text-neutral-700">{line.trim().substring(1).trim()}</span>
        </div>
      );
    }
    return <div key={index} className="text-neutral-700">{line}</div>;
  });

  return (
    <div className="space-y-2">
      {lines}
      {table && (
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full border border-neutral-300 text-sm">
            <thead>
              <tr className="bg-neutral-100">
                <th className="border border-neutral-300 px-3 py-1 text-left font-semibold">Metric</th>
                <th className="border border-neutral-300 px-3 py-1 text-left font-semibold">Value</th>
              </tr>
            </thead>
            <tbody>
              {table.map((row, i) => (
                <tr key={i}>
                  <td className="border border-neutral-300 px-3 py-1">{row.metric}</td>
                  <td className="border border-neutral-300 px-3 py-1">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Accordion context
const AccordionContext = createContext<{
  openKey: string | null;
  setOpenKey: (key: string | null) => void;
} | undefined>(undefined);

const AccordionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  return (
    <AccordionContext.Provider value={{ openKey, setOpenKey }}>
      {children}
    </AccordionContext.Provider>
  );
};

const useAccordion = () => {
  const ctx = useContext(AccordionContext);
  if (!ctx) throw new Error('useAccordion must be used within AccordionProvider');
  return ctx;
};

const FAQItem: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const { openKey, setOpenKey } = useAccordion();
  const open = openKey === title;
  return (
    <div className="mb-4">
      <button
        onClick={() => setOpenKey(open ? null : title)}
        className="w-full text-left px-6 py-4 bg-white rounded-lg shadow-soft border border-neutral-200 flex justify-between items-center"
      >
        <span className="font-medium">{title}</span>
        <ChevronDown
          className={`transition-transform duration-200 ${open ? 'transform rotate-180' : ''}`}
          size={20}
        />
      </button>
      {open && (
        <div className="px-6 py-4 bg-white border border-t-0 border-neutral-200 rounded-b-lg -mt-1">
          {children}
        </div>
      )}
    </div>
  );
};

const FaqSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} id="faq" className="section-padding bg-neutral-50 animate-on-scroll">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Everything You Need to Know
          </h2>
          <p className="text-neutral-700 text-lg">
            Get answers to commonly asked questions about our platform.
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          <AccordionProvider>
            <FAQItem title="How are test participants selected?">
              <div className="text-neutral-700">
                Tastermonial maintains an ever-growing pool of recurring users who test products for their own health benefits and interest. While we cannot guarantee product testing due to public interest, we make our best effort to balance incentives for optimal test results. Testers are free to choose which products to test, and we incentivize participation without direct reimbursement to maintain unbiased reviews.
              </div>
            </FAQItem>
            <FAQItem title="What's included in the testing protocol?">
              <div className="text-neutral-700">
                Our standard testing protocol includes product shipping, participant onboarding, 14-day continuous glucose monitoring, demographic data collection, sentiment analysis, and verified testimonial generation. Premium tiers include additional metrics and custom reporting.
              </div>
            </FAQItem>
            <FAQItem title="What do I need to provide?">
              <div className="text-neutral-700">
                We recommend brands to send their products to us so that we may pack and send it for free to the testers. This ensures a higher rate of participation and faster feedback. Furthermore, your product and experiment need to be registered in the dashboard so that users can join the experiment and be guided through the process. Our team is here to assist you in configuring the experiments in the best way for success.
              </div>
            </FAQItem>
            <FAQItem title="Can I choose specific influencers?">
              <div className="text-neutral-700">
                At this point, we do not offer the ability to choose specific influencers. However, when setting up your product experiment, you are free to clarify the intended demographic of the participants to ensure the most relevant feedback.
              </div>
            </FAQItem>
            <FAQItem title="What if my product doesn't perform well?">
              <div className="text-neutral-700">
                Transparency is our priority. You'll receive all test data regardless of outcomes. In order to ensure unbiased results, we do not reward participants to leave positive reviews.
              </div>
            </FAQItem>
            <FAQItem title="How is participant data protected?">
              <div className="text-neutral-700">
                We follow strict HIPAA-compliant protocols for all health data. Participants provide explicit consent for sharing anonymized glucose data and testimonials. Personal identifiers are never shared without express permission.
              </div>
            </FAQItem>
            <FAQItem title="What's your refund policy?">
              <div className="text-neutral-700">
                Credits that are purchased are valid for one year, after which a refund may be requested at 70% of the purchase price.
              </div>
            </FAQItem>
            <FAQItem title="How many product samples do I need to send?">
              <div className="text-neutral-700 mb-4">
                <p>Send at least 300 product samples for a target of 10 validated test completions.</p>
                <ul className="list-disc pl-6 mt-2">
                <li>We select 10 participants to sponsor the CGM and recruit up to 90 more self-pay CGM users from our Biohacker Community.</li>
                </ul>
              </div>
            </FAQItem>
            <FAQItem title="When do we see the results?">
              <div className="text-neutral-700">
                Results are updated continously as users complete experiments.
                <br />
                Most campaigns (10 validated data points) end without further incentives added, in 12 weeks.
                <br />
                By using our newsletter boost add-on (available in the dashboard), you may reach further testers and reduce the completion time to an average of 6 weeks.
                <br />
                Boost credits give your campaign priority placement across our email, app, and social channels—bringing in more testers, faster.
              </div>
            </FAQItem>
            <FAQItem title="Why rely on Tastermonial?">
              <div className="text-neutral-700">
                Tastermonial gives you trusted, third-party validation through a case-specific tool and network of testers.
                <br />
                Tastermonial provides for you, the end-to-end solution covering:
                <ul className="list-disc pl-6 mt-2">
                  <li>Sourcing of test participants</li>
                  <li>Test kit & product distribution</li>
                  <li>Data collection tool (The Tastermonial App)</li>
                  <li>Data analysis & verification</li>
                  <li>Verified testimonials</li>
                </ul>
              </div>
            </FAQItem>
            <FAQItem title="How does Tastermonial compare to a clinical trial?">
              <div className="text-neutral-700">
              <p>Faster – Get results in weeks, not years<br />
              Cheaper – Start for under $5K vs. $50K+ for clinical trials<br />
              Real-life testing – See how your product performs in everyday use<br />
              Marketing-ready – Use the data and testimonials in your campaigns<br />
              No red tape – No IRB or lengthy approvals needed<br />
              Scalable & repeatable – Run multiple tests, update formulas, test new use cases anytime.
              </p>
              <p className="mt-4">Tastermonial gives you trusted results—faster, cheaper, and designed for real-world growth.</p>
              </div>
            </FAQItem>
          </AccordionProvider>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
